import iconQrCode from  'assets/img/icon-qrcode.svg'
import IconThreeDots from "assets/img/icon-three-dots.svg?react"

import { FC, useCallback, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"


import Text from "components/Text/Text"
import AssetLabelGroup from "models/AssetLabelGroup"
import AssetService from "services/AssetService"
import { IApiLink } from "@/types"
import toastMessages from "stores/toastMessages"
import Spinner from "components/Spinner/Spinner"
import { Grid } from "components/Grid"
import { Flex, FlexItem } from "components/Flex"
import FlexibleContent from "components/FlexibleContent/FlexibleContent"
import Button from "components/Button/Button"
import { InfoDropdown, InfoDropdownList, InfoDropdownListItemButton } from "components/InfoDropdown"
import { LabelStatus } from "models/LabelGroup"
import HelpButton from "components/HelpButton/HelpButton"
import confirmModals from "stores/confirmModals"

interface IAssetLabelsProps {
    linkGetAssetLabelGroups: IApiLink
}

const AssetLabels: FC<IAssetLabelsProps> = ({
    linkGetAssetLabelGroups,
}) => {
    const { t } = useTranslation()
    const [labelGroups, setLabelGroups] = useState<AssetLabelGroup[] | null>(null)
    const [isFetching, setIsFetching] = useState(false)
    const [isUpdating, setIsUpdating] = useState(false)

    const fetchAssetLabels = useCallback(async () => {
        setIsFetching(true)
        try {
            const resp = await AssetService.getAssetLabelGroups(linkGetAssetLabelGroups)
            setLabelGroups(resp)
        } catch (e) {
            toastMessages.showSystemError()
        } finally {
            setIsFetching(false)
        }
    }, [linkGetAssetLabelGroups])

    useEffect(() => {
        fetchAssetLabels()
    }, [fetchAssetLabels])

    async function handleUnlinkLabelGroup(link: IApiLink, status: LabelStatus) {
        setIsUpdating(true)
        try {
            await AssetService.unlinkLabelGroupFromAsset(link, status)
            await fetchAssetLabels()
        } catch (e) {
            toastMessages.showSystemError()
        } finally {
            setIsUpdating(false)
        }
    }

    async function handleDeleteLabelFromLabelGroup(link: IApiLink, label: string) {
        confirmModals.show({
            title: t('common.warning'),
            maxWidth: "586px",
            theme: "danger",
            confirmButtonLabel: t('common.delete'),
            children: t('assetLabels.deleteConfirm'),
            onConfirm: async close => {
                close?.()
                setIsUpdating(true)
                try {
                    await AssetService.deleteLabelFromLabelGroup(link, label)
                    await fetchAssetLabels()
                } catch (e) {
                    toastMessages.showSystemError()
                } finally {
                    setIsUpdating(false)
                }
            },
        })
    }

    function renderLabelGoups() {
        return labelGroups?.map(labelGroup => (
            <div key={labelGroup.id} className="mb-3 border-grey-200 round-sm">
                {labelGroup.labels.map(label => (
                    <Grid columns="minmax(0, auto) 4rem"
                        gap="2.4rem"
                        className="border-grey-100-between px-2 py-2"
                        key={label}>
                        <Flex alignItems="center" gap="1rem">
                            <FlexItem shrink={0}>
                                <img src={iconQrCode} width={19} height={19} alt="" className="d-block" />
                            </FlexItem>
                            <FlexibleContent text={label} />
                        </Flex>
                        <Flex alignItems="center" justifyContent="center">
                            {(labelGroup.linkUnlinkLabelFromAsset || labelGroup.linkDeleteLabelFromLabelGroup)
                                ? <InfoDropdown
                                    label={() => (
                                        <Button
                                            theme="grey"
                                            round
                                            icon={<IconThreeDots />}
                                            disabled={isUpdating}
                                        />
                                    )}
                                    placement="bottom-end"
                                    maxWidth="38rem"
                                    content={close => (
                                        <InfoDropdownList>
                                            {labelGroup.linkUnlinkLabelFromAsset &&
                                                <>
                                                    <InfoDropdownListItemButton
                                                        onClick={() => { close(); handleUnlinkLabelGroup(labelGroup.linkUnlinkLabelFromAsset!, LabelStatus.active) }}
                                                        disabled={isUpdating}>
                                                        <Text>{t('assetLabels.unlink')}</Text>
                                                        <Text className="color-grey-600 mt-half">{t('assetLabels.unlinkDescr')}</Text>
                                                    </InfoDropdownListItemButton>
                                                    <InfoDropdownListItemButton
                                                        onClick={() => { close(); handleUnlinkLabelGroup(labelGroup.linkUnlinkLabelFromAsset!, LabelStatus.inactive) }}
                                                        disabled={isUpdating}>
                                                        <Text>{t('assetLabels.unlinkAndDeactivate')}</Text>
                                                        <Text className="color-grey-600 mt-half">{t('assetLabels.unlinkAndDeactivateDescr')}</Text>
                                                    </InfoDropdownListItemButton>
                                                </>
                                            }
                                            {labelGroup.linkDeleteLabelFromLabelGroup &&
                                                <InfoDropdownListItemButton
                                                    onClick={() => { close(); handleDeleteLabelFromLabelGroup(labelGroup.linkDeleteLabelFromLabelGroup!, label) }}

                                                    disabled={isUpdating}>
                                                    <Text>{t('assetLabels.delete')}</Text>
                                                    <Text className="color-grey-600 mt-half">{t('assetLabels.deleteDescr')}</Text>
                                                </InfoDropdownListItemButton>
                                            }
                                        </InfoDropdownList>
                                    )}
                                />
                                : <HelpButton tooltipProps={{ content: t('assetLabels.ownedByAnotherTenant'), trigger: "hover" }} />
                            }
                        </Flex>
                    </Grid>
                ))}
            </div>
        ))
    }

    return (
        <>
            <Text type="h3" className="mb-4">{t('asset.labels')}</Text>
            {!labelGroups && isFetching
                ? <Spinner className="py-8" />
                : (labelGroups && labelGroups?.length > 0
                    ? renderLabelGoups()
                    : <div className="py-6 text-center color-grey-500">{t('common.noItemsAddedYet')}</div>
                )
            }
        </>
    )
}

export default AssetLabels
